import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import type {
  Recording,
  RecordingListRawResponse,
  RecordingRaw,
  RecordingsErrorResponse,
} from "src/types";
import { isRecordingsError, RecordingsError } from "src/types";

import type { RootState } from "@/redux/root-reducer";
import endpoints from "@/utils/endpoints";
import { authenticatedFetch } from "@/utils/fetchJSON";
import { isAttendeesPage } from "@/utils/is-attendees-page";
import logger from "@/utils/logger";

import { mapRecordingRawToRecording } from "./helpers";

const get = endpoints();

export const fetchRecordingsById = createAsyncThunk<
  RecordingRaw[],
  {
    organizationExternalId?: string;
    recordingIds: string[];
    spaceExternalId?: string;
  },
  { state: RootState; rejectValue: { message: string; errors: string[] } }
>(
  "recordings/fetchRecordingsByIds",
  async (
    { organizationExternalId, recordingIds, spaceExternalId },
    thunkAPI
  ) => {
    if (recordingIds.length === 0) {
      return [];
    }

    try {
      if (isAttendeesPage()) {
        const response:
          | RecordingListRawResponse
          | RecordingsErrorResponse = await fetch(
          get("NEXT_SERVER_GET_RECORDINGS")(recordingIds),
          {
            method: "GET",
            signal: thunkAPI.signal,
          }
        ).then((res) => res.json());

        if (isRecordingsError(response)) {
          return thunkAPI.rejectWithValue({
            message: "Failed to retrieve recordings data for attendees",
            errors: response.error,
          });
        }

        return response.data;
      }

      const response = await authenticatedFetch.fetchJSON<
        RecordingListRawResponse,
        RecordingsErrorResponse
      >(
        get(`GET_RECORDINGS`)(
          organizationExternalId,
          recordingIds,
          spaceExternalId
        )
      );

      return isRecordingsError(response)
        ? thunkAPI.rejectWithValue({
            message: "Failed to retrieve recordings data for organiser",
            errors: response.error,
          })
        : response.data;
    } catch (error) {
      const originalError =
        error instanceof Error ? error : new Error(String(error));

      logger.error(originalError, {
        details: originalError.message,
        operation: "fetchRecordingsById",
      });

      return thunkAPI.rejectWithValue({
        message: "Recordings reducer caught an exception",
        errors: [`Failed with error: ${originalError.message}`],
      });
    }
  }
);

export const recordingsAdapter = createEntityAdapter<Recording>();

const recordingsSlice = createSlice({
  name: "recordings",
  initialState: recordingsAdapter.getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchRecordingsById.fulfilled, (state, action) => {
      recordingsAdapter.upsertMany(
        state,
        action.payload.map((rawRecording) =>
          mapRecordingRawToRecording(rawRecording)
        )
      );
    });
    builder.addCase(fetchRecordingsById.rejected, (_, action) => {
      logger.error(new RecordingsError(action.payload.message), {
        details: action.payload.errors.join(", "),
      });
    });
  },
});

const selectors = recordingsAdapter.getSelectors<RootState>(
  (state) => state.recordings
);

export const allRecordings = selectors.selectAll;

export const getRecordingsById = (recordingId: string) => {
  return createSelector(
    (state) => state,
    (state: RootState) => selectors.selectById(state, recordingId)
  );
};

export const getRecordingsByIds = (ids: string[]) => {
  return createSelector(allRecordings, (recordings) => {
    return ids
      .map((id) => recordings.find((recording) => recording.id === id))
      .filter(Boolean);
  });
};

export default recordingsSlice.reducer;
